import { useState, type FormEvent } from 'react';
import { grantablePermissions, type Role } from '@arcnet/contracts';
import {
  useCreateRole, useCreateTenantRole, useDeleteRole, useRoles, useUpdateRole,
} from '../../lib/api/hooks-roles.js';
import { Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { useSession } from '../../lib/auth/session.js';
import { PermissionEditor } from './PermissionEditor.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * The roles people on this account can be given (docs/33): built-in ones to read, custom ones to
 * change. A custom role made here belongs to this account.
 */

const OWNER_LABEL: Record<Role['owner'], string> = {
  builtin: 'Built in', platform: 'Platform', reseller: 'Reseller', tenant: 'This account',
};

type Editing =
  | { mode: 'new'; from?: Role }
  | { mode: 'edit' | 'view'; role: Role };

/**
 * Whose roles these are. A client's own (made on its Users page), the platform's, or a reseller's:
 * the last two are the MSP side (owner, 18 September 2026 backlog: "MSP-level roles screen"), and
 * a role there is made for one level, staff of the platform, of a reseller, or of client accounts.
 */
export type RoleOwner = { kind: 'tenant'; tenantId: string } | { kind: 'platform' } | { kind: 'reseller'; resellerId: string };

type Level = 'platform' | 'reseller' | 'tenant';
const LEVEL_LABEL: Record<Level, string> = {
  platform: 'Platform staff', reseller: 'Reseller staff', tenant: 'Client accounts',
};

export function RolesSection({ tenantId, owner: given }: { tenantId?: string; owner?: RoleOwner }) {
  const owner: RoleOwner = given ?? { kind: 'tenant', tenantId: tenantId! };
  const can = useSession((s) => s.can);
  // Everything the caller can see, then this owner's (and the built-in ones) below: a reseller's
  // client-level roles are its too, which a reseller-level listing would leave out.
  const roles = useRoles(owner.kind === 'tenant' ? { tenantId: owner.tenantId } : {});
  const remove = useDeleteRole();
  const [editing, setEditing] = useState<Editing | null>(null);

  return (
    <div style={{ marginTop: 22 }}>
      <SectionHead level="section" title="Roles" sub="What each role lets people see and do."
        action={can('role:write') && !editing && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing({ mode: 'new' })}>
            <Icon name="plus" size={13} /> New role
          </button>
        )} />

      {roles.error && <ErrorBanner error={roles.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}
      {editing && <RoleEditor owner={owner} editing={editing} onDone={() => setEditing(null)} />}

      {!editing && (
        <div className="arc-card">
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Role</th><th>Kind</th>{owner.kind !== 'tenant' && <th>For</th>}<th>Permissions</th><th>People</th><th /></tr>
              </thead>
              {roles.data === undefined ? <TableSkeleton query={roles} cols={owner.kind === 'tenant' ? 5 : 6} /> : (
                <tbody>
                  {(roles.data?.data ?? []).filter((r) => owner.kind === 'tenant' || r.owner === 'builtin'
                    || (owner.kind === 'platform' ? r.owner === 'platform' : r.reseller_id === owner.resellerId)).map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{r.name}</span>
                          {r.description && <span className="arc-subtle">{r.description}</span>}
                        </div>
                      </td>
                      <td>{OWNER_LABEL[r.owner]}</td>
                      {owner.kind !== 'tenant' && <td className="arc-muted">{LEVEL_LABEL[r.scope_level as Level] ?? r.scope_level}</td>}
                      <td className="arc-muted">{r.permissions.length}</td>
                      <td className="arc-muted">{r.holders}</td>
                      <td className="arc-td-right">
                        <div className="arc-row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          {r.editable && can('role:write') ? (
                            <RowAction kind="edit" label={`Edit ${r.name}`}
                              onClick={() => setEditing({ mode: 'edit', role: r })} />
                          ) : (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => setEditing({ mode: 'view', role: r })}>
                              View
                            </button>
                          )}
                          {can('role:write') && r.grantable && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => setEditing({ mode: 'new', from: r })}>
                              Copy
                            </button>
                          )}
                          {r.editable && can('role:write') && r.holders === 0 && (
                            <RowAction kind="delete" label={`Delete ${r.name}`}
                              onClick={() => {
                                void confirmAction({ title: `Delete the ${r.name} role?`, action: 'Delete', danger: true })
                                  .then((ok) => { if (ok) remove.mutate(r.id); });
                              }} />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          {roles.data !== undefined && roles.data.data.length === 0 && (
            <Empty icon="users" title="No roles" hint="Make one to give people exactly the access they need." />
          )}
        </div>
      )}
    </div>
  );
}

function RoleEditor({ owner, editing, onDone }: { owner: RoleOwner; editing: Editing; onDone: () => void }) {
  const sessionPerms = useSession((s) => s.permissions);
  const myLevel = useSession((s) => s.session?.scope.level ?? 'tenant');
  // What they may give: for staff that includes the client-only permissions (docs/33 §3.3).
  const held = grantablePermissions(sessionPerms, myLevel);
  const createTenant = useCreateTenantRole(owner.kind === 'tenant' ? owner.tenantId : '');
  const createMsp = useCreateRole();
  const create = owner.kind === 'tenant' ? createTenant : createMsp;
  // The level a new MSP role is for; a client's own roles, and existing roles, keep theirs.
  const levels: Level[] = owner.kind === 'platform' ? ['platform', 'reseller', 'tenant']
    : owner.kind === 'reseller' ? ['reseller', 'tenant'] : ['tenant'];
  const [roleLevel, setRoleLevel] = useState<Level>(
    editing.mode === 'new' ? ((editing.from?.scope_level as Level | undefined) ?? levels[levels.length - 1]!)
      : (editing.role.scope_level as Level));
  const update = useUpdateRole();
  const base = editing.mode === 'new' ? editing.from : editing.role;
  const readOnly = editing.mode === 'view';
  const [name, setName] = useState(editing.mode === 'new'
    ? (editing.from ? `${editing.from.name} (copy)` : '') : editing.role.name);
  const [description, setDescription] = useState(base?.description ?? '');
  // A copy keeps only what the person copying may give; the rest they could not save anyway.
  const [perms, setPerms] = useState<Set<string>>(
    new Set((base?.permissions ?? []).filter((k) => editing.mode !== 'new' || held.has(k))));

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = { name: name.trim(), description: description.trim() || null, permissions: [...perms].sort() };
    if (editing.mode === 'new') {
      if (owner.kind === 'tenant') createTenant.mutate(body, { onSuccess: onDone });
      else createMsp.mutate({ ...body, scope_level: roleLevel,
        reseller_id: owner.kind === 'reseller' ? owner.resellerId : null }, { onSuccess: onDone });
    }
    else if (editing.mode === 'edit') update.mutate({ id: editing.role.id, ...body }, { onSuccess: onDone });
  };

  return (
    <form className="arc-card arc-card-body" onSubmit={submit} style={{ marginBottom: 14 }}>
      {error && <ErrorBanner error={error} />}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="role-name">Name</label>
          <input id="role-name" className="arc-input" value={name} required maxLength={60}
            readOnly={readOnly} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="role-desc">Description</label>
          <input id="role-desc" className="arc-input" value={description} maxLength={200}
            readOnly={readOnly} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      {owner.kind !== 'tenant' && (
        <div className="arc-field" style={{ maxWidth: 280 }}>
          <label className="arc-label" htmlFor="role-level">For</label>
          <select id="role-level" className="arc-select" value={roleLevel}
            disabled={editing.mode !== 'new'} onChange={(e) => setRoleLevel(e.target.value as Level)}>
            {levels.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
          </select>
          <div className="arc-hint">Who it can be given to. Fixed once the role exists.</div>
        </div>
      )}

      <PermissionEditor level={roleLevel} value={perms} onChange={setPerms} held={held} readOnly={readOnly} />

      <div className="arc-row" style={{ gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button type="button" className="arc-btn" onClick={onDone}>{readOnly ? 'Close' : 'Cancel'}</button>
        {!readOnly && (
          <button type="submit" className="arc-btn arc-btn-primary" disabled={pending || !name.trim()}>
            {pending ? 'Saving…' : editing.mode === 'new' ? 'Create role' : 'Save'}
          </button>
        )}
      </div>
    </form>
  );
}
